// Probabilidade de Sucesso v0 (F3) — MOAT C (predição)
// Modelo determinístico: ritmo atual vs. ritmo necessário até a prova,
// ajustado por consistência. Cold start mitigado com baseline externo de
// horas por certificação. Y2 vira bayesiano com aprovados reais da base.

import type { Goal, Session } from '@app-types/domain';
import { computeConsistency } from './score';

// ===== Baseline externo (horas típicas de preparação) =====

export interface CertBaseline {
  pattern: RegExp;
  name: string;
  hours: number;
}

export const CERT_BASELINES: CertBaseline[] = [
  { pattern: /cpa[\s-]?20/i, name: 'CPA-20 · ANBIMA', hours: 120 },
  { pattern: /cpa[\s-]?10/i, name: 'CPA-10 · ANBIMA', hours: 80 },
  { pattern: /\bcea\b/i, name: 'CEA · ANBIMA', hours: 200 },
  { pattern: /\bcga\b/i, name: 'CGA · ANBIMA', hours: 300 },
  { pattern: /cfp/i, name: 'CFP', hours: 400 },
  { pattern: /cfa/i, name: 'CFA (por nível)', hours: 300 },
  { pattern: /pmp/i, name: 'PMP', hours: 300 },
  { pattern: /aws|solutions architect|saa/i, name: 'AWS SAA', hours: 120 },
  { pattern: /azure/i, name: 'Azure (associate)', hours: 100 },
  { pattern: /\boab\b/i, name: 'OAB', hours: 500 },
];

export function matchBaseline(title: string): CertBaseline | null {
  return CERT_BASELINES.find((b) => b.pattern.test(title)) ?? null;
}

// ===== Modelo =====

export interface Driver {
  label: string;
  detail: string;
  impact: 'up' | 'down' | 'flat';
}

export interface Scenario {
  id: 'keep' | 'plus1h' | 'minus20';
  label: string;
  h_per_week: number;
  point: number; // 0-100
  finish_weeks: number | null; // semanas p/ cobrir as horas restantes neste ritmo
}

export interface ProbabilityResult {
  ready: boolean;
  /** presente quando ready=false — o que falta pro modelo rodar */
  reason?: string;
  point: number; // 0-100
  range: [number, number]; // faixa de confiança 0-100
  drivers: Driver[];
  scenarios: Scenario[];
  hours_target: number;
  hours_done: number;
  days_left: number;
  current_h_per_week: number;
  needed_h_per_week: number;
  baseline_name?: string;
}

const NOT_READY: Omit<ProbabilityResult, 'reason'> = {
  ready: false,
  point: 0,
  range: [0, 0],
  drivers: [],
  scenarios: [],
  hours_target: 0,
  hours_done: 0,
  days_left: 0,
  current_h_per_week: 0,
  needed_h_per_week: 0,
};

function clampPct(n: number): number {
  return Math.max(1, Math.min(99, Math.round(n)));
}

/** curva ritmo/necessário → probabilidade. ratio 0.75 ≈ 50% · 1.0 ≈ 73% · 1.5 ≈ 95% */
function ratioToProbability(ratio: number): number {
  const k = 4;
  return 1 / (1 + Math.exp(-k * (ratio - 0.75)));
}

/** horas/semana observadas na janela recente (28d ou desde a 1ª sessão, o que for menor) */
export function observedPace(sessions: Session[], now: Date): number {
  const completed = sessions.filter((s) => s.status === 'completed' && s.completed_at);
  if (completed.length === 0) return 0;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - 28);
  const firstAt = completed
    .map((s) => new Date(s.completed_at as string).getTime())
    .reduce((a, b) => Math.min(a, b), Number.POSITIVE_INFINITY);
  const windowStart = Math.max(cutoff.getTime(), firstAt);
  const windowDays = Math.max(1, (now.getTime() - windowStart) / 86_400_000);
  const hours = completed
    .filter((s) => new Date(s.completed_at as string).getTime() >= windowStart)
    .reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
  return (hours / windowDays) * 7;
}

function scenarioAt(
  id: Scenario['id'],
  label: string,
  hPerWeek: number,
  hoursRemaining: number,
  weeksLeft: number,
  consistencyAdj: number,
): Scenario {
  const needed = weeksLeft > 0 ? hoursRemaining / weeksLeft : Number.POSITIVE_INFINITY;
  const ratio = needed > 0 ? hPerWeek / needed : 2;
  const p = hoursRemaining <= 0 ? 0.95 : ratioToProbability(ratio) + consistencyAdj;
  return {
    id,
    label,
    h_per_week: Math.round(hPerWeek * 10) / 10,
    point: clampPct(p * 100),
    finish_weeks: hPerWeek > 0 ? Math.ceil((hoursRemaining / hPerWeek) * 10) / 10 : null,
  };
}

export function computeProbability(
  goal: Goal,
  allSessions: Session[],
  now = new Date(),
): ProbabilityResult {
  const baseline = matchBaseline(goal.title);
  const hoursTarget = goal.target_hours ?? baseline?.hours ?? null;

  if (!goal.target_date) {
    return { ...NOT_READY, reason: 'Defina a data da prova na meta para ativar a predição.' };
  }
  if (hoursTarget === null) {
    return {
      ...NOT_READY,
      reason:
        'Defina as horas-alvo na meta (ou use um título de certificação conhecida, ex: CPA-20).',
    };
  }

  const target = new Date(goal.target_date);
  const daysLeft = Math.ceil((target.getTime() - now.getTime()) / 86_400_000);
  if (daysLeft <= 0) {
    return { ...NOT_READY, reason: 'A data da prova já passou — atualize a meta.' };
  }

  const sessions = allSessions.filter((s) => s.goal_id === goal.id);
  const completed = sessions.filter((s) => s.status === 'completed');
  const hoursDone = completed.reduce((sum, s) => sum + (s.actual_duration_min || 0) / 60, 0);
  const hoursRemaining = Math.max(0, hoursTarget - hoursDone);
  const weeksLeft = daysLeft / 7;
  const neededHPerWeek = hoursRemaining / weeksLeft;
  const currentHPerWeek = observedPace(sessions, now);

  // consistência (score 0-100) modula ±10pp — quem estuda todo dia erra menos a previsão
  const consistency = computeConsistency(sessions, now, 5);
  const consistencyAdj =
    consistency === null ? 0 : Math.max(-0.1, Math.min(0.1, (consistency - 70) / 200));

  const ratio = neededHPerWeek > 0 ? currentHPerWeek / neededHPerWeek : 2;
  const raw = hoursRemaining <= 0 ? 0.95 : ratioToProbability(ratio) + consistencyAdj;
  const point = clampPct(raw * 100);

  // faixa de confiança: encolhe com mais dias de dado observado
  const dataDays = new Set(
    completed.filter((s) => s.completed_at).map((s) => (s.completed_at as string).slice(0, 10)),
  ).size;
  const half = Math.max(6, Math.round(22 - dataDays * 0.8));
  const range: [number, number] = [clampPct(point - half), clampPct(point + half)];

  // drivers explicáveis
  const drivers: Driver[] = [];
  if (hoursRemaining <= 0) {
    drivers.push({
      label: 'Horas-alvo atingidas',
      detail: `${hoursDone.toFixed(0)}h feitas de ${hoursTarget}h`,
      impact: 'up',
    });
  } else {
    drivers.push({
      label: 'Ritmo',
      detail: `${currentHPerWeek.toFixed(1)}h/sem atual vs. ${neededHPerWeek.toFixed(1)}h/sem necessárias`,
      impact: ratio >= 1 ? 'up' : ratio >= 0.7 ? 'flat' : 'down',
    });
  }
  drivers.push({
    label: 'Tempo até a prova',
    detail: `${daysLeft} dias (${Math.floor(weeksLeft)} semanas)`,
    impact: 'flat',
  });
  if (consistency !== null) {
    drivers.push({
      label: 'Consistência',
      detail: `${consistency}/100 nos últimos 14 dias`,
      impact: consistencyAdj > 0.01 ? 'up' : consistencyAdj < -0.01 ? 'down' : 'flat',
    });
  }
  if (completed.length === 0) {
    drivers.push({
      label: 'Sem histórico ainda',
      detail: 'A predição afina conforme você estuda — comece uma sessão.',
      impact: 'down',
    });
  }

  const scenarios: Scenario[] = [
    scenarioAt(
      'keep',
      'Mantendo o ritmo',
      currentHPerWeek,
      hoursRemaining,
      weeksLeft,
      consistencyAdj,
    ),
    scenarioAt(
      'plus1h',
      '+1h por dia',
      currentHPerWeek + 7,
      hoursRemaining,
      weeksLeft,
      consistencyAdj,
    ),
    scenarioAt(
      'minus20',
      'Ritmo cai 20%',
      currentHPerWeek * 0.8,
      hoursRemaining,
      weeksLeft,
      consistencyAdj,
    ),
  ];

  return {
    ready: true,
    point,
    range,
    drivers,
    scenarios,
    hours_target: hoursTarget,
    hours_done: Math.round(hoursDone * 10) / 10,
    days_left: daysLeft,
    current_h_per_week: Math.round(currentHPerWeek * 10) / 10,
    needed_h_per_week: Math.round(neededHPerWeek * 10) / 10,
    baseline_name: goal.target_hours ? undefined : baseline?.name,
  };
}
